import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries, getDisplayName } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, MessagesSquare, Pencil, Trash2, Loader2, Reply, ChevronDown, ChevronUp, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import ReactMarkdown from "react-markdown";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeRaw from "rehype-raw";
import type { Comment, BoardMember } from "./task-types";
import type { Plugin } from "unified";
import type { Root, Text, PhrasingContent } from "mdast";
import { visit } from "unist-util-visit";

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []).filter(
    (tag: string) => !["h1", "h2", "h3", "h4", "h5", "h6", "img"].includes(tag)
  ), "span"],
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span || []), "className", "class", "dataTestid", "dataMention", "data-testid", "data-mention"],
  },
};

type UserMap = Map<string, { displayName: string; username: string }>;

const remarkMentions: Plugin<[{ userMap: UserMap }], Root> = (options) => {
  const { userMap } = options;
  return (tree) => {
    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || index === undefined) return;
      if ((parent.type as string) === "code" || (parent.type as string) === "inlineCode") return;

      const mentionRegex = /@([a-zA-Z0-9_.-]+)/g;
      const value = node.value;
      let match;
      const parts: PhrasingContent[] = [];
      let lastIndex = 0;

      while ((match = mentionRegex.exec(value)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }

        const username = match[1].toLowerCase();
        const userInfo = userMap.get(username);
        const displayText = userInfo ? `@${userInfo.displayName}` : `@${match[1]}`;

        parts.push({
          type: "html",
          value: `<span class="bg-primary/10 text-primary rounded px-0.5 font-medium" data-testid="mention-${username}" data-mention="${username}">${displayText}</span>`,
        } as any);

        lastIndex = match.index + match[0].length;
      }

      if (parts.length > 0) {
        if (lastIndex < value.length) {
          parts.push({ type: "text", value: value.slice(lastIndex) });
        }
        parent.children.splice(index, 1, ...parts);
      }
    });
  };
};

export interface MentionableUser {
  userId: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

function MentionDropdown({
  users,
  selectedIndex,
  onSelect,
  testIdPrefix,
}: {
  users: MentionableUser[];
  selectedIndex: number;
  onSelect: (user: MentionableUser) => void;
  testIdPrefix: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.children[selectedIndex] as HTMLElement;
      if (selected) selected.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  return (
    <div
      ref={listRef}
      className="absolute z-50 bottom-full mb-1 left-0 w-64 max-h-48 overflow-y-auto border rounded-md bg-popover shadow-md"
      data-testid={`${testIdPrefix}-mention-dropdown`}
    >
      {users.map((u, i) => (
        <button
          key={u.userId}
          type="button"
          className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left ${i === selectedIndex ? "bg-accent" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(u);
          }}
          data-testid={`mention-option-${u.username}`}
        >
          <Avatar className="h-5 w-5 shrink-0">
            <AvatarFallback className="text-[8px]">
              {getDisplayName(u.firstName, u.lastName, u.username).slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="truncate font-medium">{getDisplayName(u.firstName, u.lastName, u.username)}</span>
            <span className="truncate text-xs text-muted-foreground">@{u.username}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

const MENTION_TRIGGER_REGEX = /(?:^|\s)@([a-zA-Z0-9_.-]*)$/;

function CommentInput({
  value,
  onChange,
  placeholder,
  textareaTestId,
  mentionableUsers,
  userMap,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  textareaTestId: string;
  mentionableUsers?: MentionableUser[];
  userMap?: UserMap;
}) {
  const [previewMode, setPreviewMode] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredMentionUsers = useMemo(() => {
    if (mentionQuery === null || !mentionableUsers || mentionableUsers.length === 0) return [];
    const q = mentionQuery.toLowerCase();
    return mentionableUsers
      .filter(u => {
        if (!q) return true;
        return (
          u.username.toLowerCase().includes(q) ||
          (u.firstName && u.firstName.toLowerCase().includes(q)) ||
          (u.lastName && u.lastName.toLowerCase().includes(q))
        );
      })
      .slice(0, 8);
  }, [mentionQuery, mentionableUsers]);

  const showDropdown = filteredMentionUsers.length > 0;

  const updateMentionState = useCallback((textarea: HTMLTextAreaElement) => {
    if (!mentionableUsers || mentionableUsers.length === 0) {
      setMentionQuery(null);
      return;
    }
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = textarea.value.slice(0, cursorPos);
    const match = textBeforeCursor.match(MENTION_TRIGGER_REGEX);
    if (match) {
      setMentionQuery(match[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  }, [mentionableUsers]);

  const insertMention = useCallback((user: MentionableUser) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const match = textBeforeCursor.match(MENTION_TRIGGER_REGEX);
    if (!match) return;

    const mentionStart = cursorPos - match[1].length - 1;
    const before = value.slice(0, mentionStart);
    const after = value.slice(cursorPos);
    const insertText = `@${user.username} `;
    const newValue = before + insertText + after;
    onChange(newValue);
    setMentionQuery(null);

    requestAnimationFrame(() => {
      const newPos = mentionStart + insertText.length;
      textarea.selectionStart = newPos;
      textarea.selectionEnd = newPos;
      textarea.focus();
    });
  }, [value, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIndex(prev => (prev + 1) % filteredMentionUsers.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIndex(prev => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(filteredMentionUsers[mentionIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMentionQuery(null);
    }
  }, [showDropdown, filteredMentionUsers, mentionIndex, insertMention]);

  const remarkPlugins = useMemo(() => {
    if (!userMap) return [];
    return [[remarkMentions, { userMap }] as any];
  }, [userMap]);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-xs">
        <button
          type="button"
          className={`px-2 py-0.5 rounded-md ${!previewMode ? "bg-muted font-medium" : "text-muted-foreground"}`}
          onClick={() => setPreviewMode(false)}
          data-testid={`${textareaTestId}-tab-write`}
        >
          Write
        </button>
        <button
          type="button"
          className={`px-2 py-0.5 rounded-md ${previewMode ? "bg-muted font-medium" : "text-muted-foreground"}`}
          onClick={() => setPreviewMode(true)}
          data-testid={`${textareaTestId}-tab-preview`}
        >
          Preview
        </button>
      </div>
      {previewMode ? (
        <div className="border rounded-md p-3 min-h-[60px] text-sm">
          {value.trim() ? (
            <MarkdownBody body={value} testId={`${textareaTestId}-preview`} userMap={userMap} />
          ) : (
            <span className="text-muted-foreground text-sm">Nothing to preview</span>
          )}
        </div>
      ) : (
        <div className="relative">
          {showDropdown && (
            <MentionDropdown
              users={filteredMentionUsers}
              selectedIndex={mentionIndex}
              onSelect={insertMention}
              testIdPrefix={textareaTestId}
            />
          )}
          <Textarea
            ref={textareaRef}
            placeholder={placeholder}
            value={value}
            onChange={e => {
              onChange(e.target.value);
              updateMentionState(e.target);
            }}
            onKeyDown={handleKeyDown}
            onSelect={(e) => updateMentionState(e.currentTarget as HTMLTextAreaElement)}
            className="resize-none text-sm min-h-[60px]"
            maxLength={5000}
            data-testid={textareaTestId}
          />
        </div>
      )}
    </div>
  );
}

function MarkdownBody({ body, testId, userMap }: { body: string; testId: string; userMap?: UserMap }) {
  const remarkPlugins = useMemo(() => {
    if (!userMap || userMap.size === 0) return [];
    return [[remarkMentions, { userMap }] as any];
  }, [userMap]);

  const rehypePlugins = useMemo(() => {
    if (!userMap || userMap.size === 0) return [[rehypeSanitize, sanitizeSchema]];
    return [rehypeRaw, [rehypeSanitize, sanitizeSchema]] as any;
  }, [userMap]);

  return (
    <div
      className="prose prose-sm dark:prose-invert max-w-none text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
      data-testid={testId}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}

const REPLY_COLLAPSE_THRESHOLD = 3;

interface CommentSectionProps {
  entityType: string;
  entityId: string;
  canComment: boolean;
  currentUserId: string;
  mentionableUsers?: MentionableUser[];
}

export function CommentSection({ entityType, entityId, canComment, currentUserId, mentionableUsers }: CommentSectionProps) {
  const { toast } = useToast();
  const [newCommentText, setNewCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const isUserActive = !!editingCommentId || !!replyingToId;

  const userMap = useMemo<UserMap>(() => {
    const map = new Map<string, { displayName: string; username: string }>();
    if (mentionableUsers) {
      for (const u of mentionableUsers) {
        map.set(u.username.toLowerCase(), {
          displayName: getDisplayName(u.firstName, u.lastName, u.username),
          username: u.username,
        });
      }
    }
    return map;
  }, [mentionableUsers]);

  const commentsQueryKey = ["/api/comments", entityType, entityId];

  const { data: commentsData, isLoading } = useQuery<{ comments: Comment[] }>({
    queryKey: commentsQueryKey,
    refetchInterval: isUserActive ? false : 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (
      !isLoading &&
      commentsData &&
      !hasScrolledRef.current &&
      window.location.hash === "#comments"
    ) {
      hasScrolledRef.current = true;
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      });
    }
  }, [isLoading, commentsData]);

  const allComments = commentsData?.comments ?? [];

  const topLevelComments = allComments.filter(c => !c.parentCommentId);
  const repliesByParent = new Map<string, Comment[]>();
  for (const c of allComments) {
    if (c.parentCommentId) {
      const existing = repliesByParent.get(c.parentCommentId) ?? [];
      existing.push(c);
      repliesByParent.set(c.parentCommentId, existing);
    }
  }

  const totalCommentCount = allComments.length;

  function invalidateComments() {
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    if (entityType === "task") {
      invalidateTaskQueries();
    }
  }

  const addCommentMutation = useMutation({
    mutationFn: async (data: { body: string; parentCommentId?: string }) =>
      apiRequest("POST", `/api/comments/${entityType}/${entityId}`, data),
    onSuccess: () => {
      invalidateComments();
      setNewCommentText("");
      setReplyingToId(null);
      setReplyText("");
    },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const editCommentMutation = useMutation({
    mutationFn: async ({ commentId, body }: { commentId: string; body: string }) =>
      apiRequest("PATCH", `/api/comments/${commentId}`, { body }),
    onSuccess: () => {
      invalidateComments();
      setEditingCommentId(null);
      setEditCommentText("");
    },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) =>
      apiRequest("DELETE", `/api/comments/${commentId}`),
    onSuccess: () => { invalidateComments(); },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  function canModifyComment(comment: Comment): boolean {
    return !!comment.createdBy && comment.createdBy === currentUserId;
  }

  function startEditing(comment: Comment) {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.body);
    setReplyingToId(null);
    setReplyText("");
  }

  function cancelEditing() {
    setEditingCommentId(null);
    setEditCommentText("");
  }

  function startReplying(commentId: string) {
    setReplyingToId(commentId);
    setReplyText("");
    setEditingCommentId(null);
    setEditCommentText("");
  }

  function cancelReplying() {
    setReplyingToId(null);
    setReplyText("");
  }

  function toggleThread(parentId: string) {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  }

  function renderCommentActions(c: Comment) {
    const hasAnyAction = (canComment || canModifyComment(c)) && editingCommentId !== c.id;
    if (!hasAnyAction) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            data-testid={`button-comment-actions-${c.id}`}
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canComment && (
            <DropdownMenuItem
              onClick={() => startReplying(c.parentCommentId ?? c.id)}
              data-testid={`button-reply-comment-${c.id}`}
            >
              <Reply className="w-3.5 h-3.5 mr-2" />
              Reply
            </DropdownMenuItem>
          )}
          {canModifyComment(c) && (
            <>
              <DropdownMenuItem
                onClick={() => startEditing(c)}
                data-testid={`button-edit-comment-${c.id}`}
              >
                <Pencil className="w-3.5 h-3.5 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDeleteConfirmId(c.id)}
                data-testid={`button-delete-comment-${c.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function renderComment(c: Comment, isReply: boolean) {
    const avatarSize = isReply ? "h-5 w-5" : "h-6 w-6";
    const avatarTextSize = isReply ? "text-[8px]" : "text-[9px]";

    return (
      <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
        <Avatar className={`${avatarSize} shrink-0 mt-0.5`}>
          {c.authorAvatarUrl && (
            <AvatarImage src={getAvatarSrc(c.authorAvatarUrl, c.createdBy)} alt={getDisplayName(c.authorFirstName, c.authorLastName, c.authorUsername)} />
          )}
          <AvatarFallback className={avatarTextSize}>
            {getDisplayName(c.authorFirstName, c.authorLastName, c.authorUsername).slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium" data-testid={`text-comment-author-${c.id}`}>
              {getDisplayName(c.authorFirstName, c.authorLastName, c.authorUsername)}
            </span>
            <span className="text-xs text-muted-foreground" data-testid={`text-comment-time-${c.id}`}>
              {new Date(c.createdAt).toLocaleString()}
            </span>
            {c.updatedAt && (
              <span className="text-xs text-muted-foreground italic" data-testid={`text-comment-edited-${c.id}`}>
                (edited)
              </span>
            )}
            <div className="flex items-center gap-0.5 ml-auto" style={{ visibility: "visible" }}>
              {renderCommentActions(c)}
            </div>
          </div>

          {editingCommentId === c.id ? (
            <div className="mt-1 space-y-2">
              <CommentInput
                value={editCommentText}
                onChange={setEditCommentText}
                textareaTestId={`textarea-edit-comment-${c.id}`}
                mentionableUsers={mentionableUsers}
                userMap={userMap}
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={!editCommentText.trim() || editCommentMutation.isPending}
                  onClick={() => editCommentMutation.mutate({ commentId: c.id, body: editCommentText.trim() })}
                  data-testid={`button-save-comment-${c.id}`}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={cancelEditing}
                  data-testid={`button-cancel-edit-comment-${c.id}`}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <MarkdownBody body={c.body} testId={`text-comment-body-${c.id}`} userMap={userMap} />
          )}
        </div>
      </div>
    );
  }

  function renderThread(parentComment: Comment) {
    const replies = repliesByParent.get(parentComment.id) ?? [];
    const hasReplies = replies.length > 0;
    const needsCollapse = replies.length > REPLY_COLLAPSE_THRESHOLD;
    const isExpanded = expandedThreads.has(parentComment.id);
    const visibleReplies = needsCollapse && !isExpanded
      ? replies.slice(0, REPLY_COLLAPSE_THRESHOLD)
      : replies;
    const hiddenCount = needsCollapse ? replies.length - REPLY_COLLAPSE_THRESHOLD : 0;

    return (
      <div key={parentComment.id}>
        {renderComment(parentComment, false)}

        {hasReplies && (
          <div className="ml-4 mt-2 pl-3 border-l-2 border-border space-y-2" data-testid={`thread-${parentComment.id}`}>
            {visibleReplies.map(reply => renderComment(reply, true))}

            {needsCollapse && !isExpanded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleThread(parentComment.id)}
                className="text-xs text-muted-foreground"
                data-testid={`button-expand-thread-${parentComment.id}`}
              >
                <ChevronDown className="w-3 h-3 mr-1" />
                Show {hiddenCount} more {hiddenCount === 1 ? "reply" : "replies"}
              </Button>
            )}
            {needsCollapse && isExpanded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleThread(parentComment.id)}
                className="text-xs text-muted-foreground"
                data-testid={`button-collapse-thread-${parentComment.id}`}
              >
                <ChevronUp className="w-3 h-3 mr-1" />
                Show fewer replies
              </Button>
            )}
          </div>
        )}

        {replyingToId === parentComment.id && canComment && (
          <div className="ml-4 mt-2 pl-3 border-l-2 border-primary/30 space-y-2" data-testid={`reply-input-${parentComment.id}`}>
            <CommentInput
              value={replyText}
              onChange={setReplyText}
              placeholder="Write a reply..."
              textareaTestId={`textarea-reply-${parentComment.id}`}
              mentionableUsers={mentionableUsers}
              userMap={userMap}
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={!replyText.trim() || addCommentMutation.isPending}
                onClick={() => replyText.trim() && addCommentMutation.mutate({ body: replyText.trim(), parentCommentId: parentComment.id })}
                data-testid={`button-submit-reply-${parentComment.id}`}
              >
                <Send className="w-3 h-3 mr-1" />
                Reply
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelReplying}
                data-testid={`button-cancel-reply-${parentComment.id}`}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={sectionRef} id="comments" className="space-y-3">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <MessagesSquare className="w-3 h-3" /> Comments
        {!isLoading && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1" data-testid="badge-comments-count">
            {totalCommentCount}
          </Badge>
        )}
      </Label>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {topLevelComments.map(c => renderThread(c))}
        </div>
      )}

      {canComment && (
        <div className="space-y-2">
          <CommentInput
            value={newCommentText}
            onChange={setNewCommentText}
            placeholder="Write a comment... (Markdown supported)"
            textareaTestId="textarea-new-comment"
            mentionableUsers={mentionableUsers}
            userMap={userMap}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={!newCommentText.trim() || addCommentMutation.isPending}
              onClick={() => newCommentText.trim() && addCommentMutation.mutate({ body: newCommentText.trim() })}
              data-testid="button-submit-comment"
            >
              <Send className="w-3 h-3 mr-1" />
              Comment
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirmId && repliesByParent.has(deleteConfirmId)
                ? "This will also delete all replies to this comment. This action cannot be undone."
                : "This action cannot be undone. The comment will be permanently deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-comment">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deleteConfirmId) deleteCommentMutation.mutate(deleteConfirmId); setDeleteConfirmId(null); }}
              data-testid="button-confirm-delete-comment"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
